import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { cn } from '@repo/lib/cn';
import { format, isValid, parseISO } from 'date-fns';
import { CheckCircle2Icon, DownloadIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { UsersMassImportRow } from '@/pages/_main/manage/user/ManageUsersMassImportPage';

import UserMassImportInfo from '@/components/features/manage/manage-users/UserMassImportInfo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

/**
 * Props for the UserMassImportContent component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(open: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {Dispatch<SetStateAction<UsersMassImportRow[]>>} setData - Setter for imported user data.
 */
interface UserMassImportContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setData: Dispatch<SetStateAction<UsersMassImportRow[]>>;
}

export default function UserMassImportContent(userMassImportContentProps: UserMassImportContentProps) {
  const { open, onOpenChange, setData } = userMassImportContentProps;
  const inputRef = useRef<HTMLInputElement>(null);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [fileRawData, setFileRawData] = useState<string | null>(null);
  const [tableData, setTableData] = useState<UsersMassImportRow[]>([]);
  const [separator, setSeparator] = useState<string>(',');
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = () => {
    setData((curr) => [...curr, ...tableData]);
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
    setTableData([]);
    setFileRawData(null);
    setFileName('');
  };

  const handleFileInputClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const parseFile = async (file: File): Promise<string> => {
    return (await file.text()).trim();
  };

  const handleLoadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const parsedFile: string = await parseFile(file);
    setError(null);
    setFileRawData(parsedFile);
    const parsedTableData = handleParseStringToUsersList(parsedFile);
    if (parsedTableData) {
      setTableData(parsedTableData);
    }
  };

  const handleParseStringToUsersList = (fileString: string, newSeparator?: string): UsersMassImportRow[] | undefined => {
    const iter: string[] = fileString.split(/\r?\n/).filter(Boolean);
    const sep: string = newSeparator ?? separator;
    const strip = (s: string) =>
      s
        .replace(/^\uFEFF/, '')
        .replace(/^["']|["']$/g, '')
        .trim();

    if (!iter.length) {
      setError('Plik jest pusty');
      setTableData([]);
      return;
    }

    const header: string[] = strip(iter[0] ?? '').split(sep);

    const headers = [
      {
        field: 'name',
        pl: ['imię', 'imie'],
        en: ['name'],
        obligatory: true,
      },
      {
        field: 'surname',
        pl: ['nazwisko'],
        en: ['surname'],
        obligatory: true,
      },
      {
        field: 'birthdate',
        pl: ['data urodzenia'],
        en: ['birthdate', 'birthday'],
      },
      {
        field: 'email',
        pl: ['email', 'e-mail', 'adres e-mail'],
        en: ['email', 'e-mail'],
      },
      {
        field: 'phone',
        pl: ['telefon', 'nr tel', 'numer telefonu'],
        en: ['phone number', 'phone'],
      },
      {
        field: 'lvl',
        pl: ['poświadczenia', 'rola'],
        en: ['lvl', 'permissions'],
      },
    ];

    const labels: string[] = [];

    for (const [idx, rawLabel] of header.entries()) {
      const normalized = strip(rawLabel).toLowerCase();
      for (const field of headers) {
        if ([...field.pl, ...field.en].includes(normalized)) {
          labels[idx] = field.field;
          break;
        }
      }
    }
    if (!labels.length) {
      setError('Nie wykryto poprawnych nagłówków kolumn');
      setTableData([]);
      return;
    }

    setError(null);

    const users: UsersMassImportRow[] = [];

    for (let i = 1; i < iter.length; i++) {
      const lineParts: string[] = strip(iter[i] ?? '').split(sep);
      const user: Partial<UsersMassImportRow> = {};
      user.id = uuidv4();

      for (const [idx, rawCell] of lineParts.entries()) {
        const cell = strip(rawCell);
        if (labels[idx] && cell) {
          user[labels[idx] as keyof UsersMassImportRow] = cell;
        }
      }

      if (user.lvl == undefined) {
        user.lvl = '0';
      }

      if (Object.keys(user).length > 1) {
        users.push(user as UsersMassImportRow);
      }
    }
    return users;
  };

  const renderRole = (lvl?: string) => {
    if (lvl === '0') {
      return (
        <Badge size={'sm'} variant={'blue'}>
          Uczeń
        </Badge>
      );
    }
    if (lvl === '1') {
      return (
        <Badge size={'sm'} variant={'turquoise'}>
          Nauczyciel
        </Badge>
      );
    }
    if (lvl === '2') {
      return (
        <Badge size={'sm'} variant={'purple'}>
          Administrator
        </Badge>
      );
    }
    return null;
  };

  const renderDate = (date?: string) => {
    if (typeof date === 'string' && isValid(parseISO(date))) {
      return format(new Date(date), 'dd.MM.yyyy');
    }
    return date ?? '';
  };

  useEffect(() => {
    if (separator && fileRawData) {
      const parsedTableData = handleParseStringToUsersList(fileRawData, separator);
      if (parsedTableData) {
        setTableData(parsedTableData);
      }
    }
  }, [separator, fileRawData]);

  return (
    <>
      <UserMassImportInfo open={openInfo} onOpenChange={setOpenInfo} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={'w-[65vw] sm:w-[calc(100vw-2rem)]'}>
          <DialogBody>
            <DialogHeader className={'border-border-primary flex flex-col border-b'}>
              <DialogTitle>Importuj dane</DialogTitle>
              <DialogDescription className={'max-w-xl'}>
                Dane użytkownika są importowane z pliku CSV. Plik powinien zawierać kolumny o podanych nazwach —{' '}
                <Button variant={'linkSecondary'} className={'text-sm'} onClick={() => setOpenInfo(true)}>
                  Sprawdź tutaj.
                </Button>
              </DialogDescription>
            </DialogHeader>
            <div className={'flex flex-col gap-3 overflow-auto p-4'}>
              <input ref={inputRef} className={'hidden'} type={'file'} name={'file'} onChange={handleLoadFile} accept={'.csv'} />
              <div className={'flex gap-2 py-1'}>
                <Select onValueChange={setSeparator} value={separator}>
                  <SelectTrigger className={'w-44'}>
                    <SelectValue placeholder="Separator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Separator</SelectLabel>
                      <SelectItem value={';'}>;</SelectItem>
                      <SelectItem value={','}>,</SelectItem>
                      <SelectItem value={' '}>*spacja*</SelectItem>
                      <SelectItem value={'\t'}>*tabulator*</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Button onClick={handleFileInputClick} variant={'outline'}>
                  <DownloadIcon />
                  {fileName ? <p className={'truncate'}>{fileName}</p> : 'Wybierz plik'}
                </Button>
              </div>
              <div className={'py-1'}>
                <div className="border-border-primary bg-background relative h-full w-full overflow-x-auto rounded-2xl border">
                  <Table className={'h-fit text-center'}>
                    <colgroup>
                      <col className="w-[15%]" />
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                      <col className="w-[15%]" />
                      <col className="w-[15%]" />
                      <col className="w-[15%]" />
                    </colgroup>
                    <TableCaption className={cn('my-4', tableData.length && 'hidden', error && 'text-text-destructive')}>
                      {error ? error : 'Wybierz plik do załadowania oraz wybierz separator'}
                    </TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Stanowisko</TableHead>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Imię</TableHead>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Nazwisko</TableHead>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Email</TableHead>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Nr.tel</TableHead>
                        <TableHead className="p-1 text-center text-xs text-nowrap">Data ur.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableData.map((row) => (
                        <TableRow key={row.id} className={'h-8'}>
                          <TableCell className="text-xs">{renderRole(row.lvl)}</TableCell>
                          <TableCell className="text-xs">{row.name}</TableCell>
                          <TableCell className="text-xs">{row.surname}</TableCell>
                          <TableCell className="text-xs">{row.email}</TableCell>
                          <TableCell className="text-xs">{row.phone}</TableCell>
                          <TableCell className="text-xs">{renderDate(row.birthdate ?? undefined)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter className={'border-border-primary bg-accent dark:bg-card border-t'}>
            <Button variant={'tertiary'} onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={onSubmit} disabled={!tableData.length}>
              <CheckCircle2Icon />
              Dodaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
